/**
 * Application-wide constants
 */

/**
 * API Configuration
 *
 * API_BASE_URL defaults to empty string for same-origin requests.
 * - In development: Vite proxy forwards /api requests to localhost:8787
 * - In production: Cloudflare Worker serves both frontend and API on same origin
 *
 * Override with VITE_API_URL environment variable if needed (e.g., for custom deployments)
 */
export const API_BASE_URL =
  (import.meta as { env?: Record<string, string> }).env?.VITE_API_URL || "";
export const API_TIMEOUT = 30000; // 30 seconds

/**
 * Application Metadata
 */
export const APP_NAME = "Solana Web3 Scaffold";
export const APP_DESCRIPTION = "Solana Web3 Application Scaffold";
export const APP_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : "http://localhost:5173";
export const APP_ICON_URL = `${typeof window !== "undefined" ? window.location.origin : "http://localhost:5173"}/logo.png`;

/**
 * Solana RPC Configuration
 *
 * The frontend uses the backend RPC proxy at /api/rpc for all read operations.
 * This provides:
 * - Rate limiting and request optimization
 * - Caching at the edge
 * - Consistent error handling
 * - Reduced direct RPC costs
 */
const SOLANA_RPC_ENDPOINT_PATH = "/api/rpc";

/**
 * Get the full Solana RPC endpoint URL
 */
export const getSolanaRpcEndpoint = (): string => {
  const endpoint = SOLANA_RPC_ENDPOINT_PATH;

  if (endpoint.startsWith("http://") || endpoint.startsWith("https://")) {
    return endpoint;
  }

  if (typeof window !== "undefined") {
    return `${window.location.origin}${endpoint}`;
  }

  return endpoint;
};

/**
 * Export the path for backward compatibility
 */
export const SOLANA_RPC_ENDPOINT = SOLANA_RPC_ENDPOINT_PATH;

/**
 * Solana Network
 */
export const SOLANA_NETWORK =
  (import.meta as { env?: Record<string, string> }).env?.VITE_SOLANA_NETWORK ||
  "devnet";

/**
 * Solana Constants
 */
export const LAMPORTS_PER_SOL = 1_000_000_000;
export const DEFAULT_TOKEN_DECIMALS = 9;

/**
 * Transaction Settings
 */
export const TRANSACTION_CONFIRMATION_COMMITMENT = "confirmed" as const;
export const TRANSACTION_TIMEOUT = 60000; // 60 seconds

/**
 * Common Solana Token Mints.
 *
 * USDC / USDT are env-driven so each cluster can point at the right mint:
 *   - Mainnet: leave VITE_*_MINT unset; defaults below = Circle/Tether mainnet
 *   - Devnet:  run `scripts/create-devnet-mock-stablecoins.sh`, paste the
 *              output into the cluster's .env file (see CLAUDE.md "v1 =
 *              devnet (P1 decision)")
 *   - Local / testnet: same pattern
 *
 * Wrapped SOL's mint is the same on every cluster, so it stays hardcoded.
 *
 * The fallback to mainnet addresses is intentional: a production build that
 * forgets to set the env still resolves to a real mint (even if it's not the
 * "right" one for the cluster), avoiding undefined / empty-string mint bugs
 * downstream in the token-account match logic.
 */
const env = (import.meta as { env?: Record<string, string> }).env ?? {};
const MAINNET_USDC = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
const MAINNET_USDT = "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB";

export const COMMON_TOKEN_MINTS = {
  USDC: env.VITE_USDC_MINT || MAINNET_USDC,
  USDT: env.VITE_USDT_MINT || MAINNET_USDT,
  SOL: "So11111111111111111111111111111111111111112",
};

/**
 * Pagination
 */
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

/**
 * Cache TTL (Time To Live) in milliseconds
 */
export const CACHE_TTL = {
  ASSETS: 5 * 60 * 1000, // 5 minutes
  PRICES: 30 * 1000, // 30 seconds
  USER_DATA: 60 * 1000, // 1 minute
} as const;
